import type { ScannedCode } from "./connect-code";
import type { CameraFailure } from "./scanner";

/**
 * Why the live scan stopped: the camera, a decoder that would not load, a
 * camera feed that ended under the scan (another app took the camera, or it
 * was unplugged), or a decoder that failed on every frame.
 */
export type ScanFailure = CameraFailure | "decoder" | "ended" | "reader";

/**
 * What the scan sheet shows, as a reducer so the transitions are tested
 * without a camera. The camera starts only from a tap (START); a found code
 * ends the scan, and the sheet files CONNECT with it.
 */
export type ScannerState =
  | { kind: "idle" }
  | { kind: "starting" }
  | { kind: "scanning" }
  | { kind: "decoding-photo" }
  | { kind: "no-code-in-photo" }
  | { kind: "camera-failed"; reason: ScanFailure }
  | { kind: "found"; scanned: ScannedCode };

export type ScannerEvent =
  | { type: "START" }
  | { type: "CAMERA_READY" }
  | { type: "CAMERA_FAILED"; reason: ScanFailure }
  | { type: "FOUND"; scanned: ScannedCode }
  /** Typed by hand; accepted whatever the camera is doing. */
  | { type: "ENTERED"; scanned: ScannedCode }
  | { type: "PHOTO_CHOSEN" }
  | { type: "PHOTO_DECODED"; scanned: ScannedCode | null }
  /** The sheet closed or the page went to the background. */
  | { type: "STOP" }
  /** "Scan another": back to the start, the found code forgotten. */
  | { type: "RESET" };

export const INITIAL_SCANNER_STATE: ScannerState = { kind: "idle" };

export function scannerReducer(
  state: ScannerState,
  event: ScannerEvent,
): ScannerState {
  switch (event.type) {
    case "START":
      return state.kind === "starting" || state.kind === "scanning"
        ? state
        : { kind: "starting" };
    case "CAMERA_READY":
      return state.kind === "starting" ? { kind: "scanning" } : state;
    case "CAMERA_FAILED":
      // From a running scan too: leaving `scanning` is what stops the camera,
      // so a failure there must never leave it on behind an error.
      return state.kind === "starting" || state.kind === "scanning"
        ? { kind: "camera-failed", reason: event.reason }
        : state;
    case "FOUND":
      // Only a running scan can find something; a late frame after the sheet
      // stopped must not file a request.
      return state.kind === "scanning"
        ? { kind: "found", scanned: event.scanned }
        : state;
    case "ENTERED":
      return state.kind === "found"
        ? state
        : { kind: "found", scanned: event.scanned };
    case "PHOTO_CHOSEN":
      return state.kind === "found" ? state : { kind: "decoding-photo" };
    case "PHOTO_DECODED":
      if (state.kind !== "decoding-photo") return state;
      return event.scanned
        ? { kind: "found", scanned: event.scanned }
        : { kind: "no-code-in-photo" };
    case "STOP":
      return state.kind === "found" ? state : { kind: "idle" };
    case "RESET":
      return { kind: "idle" };
  }
}

/** Whether the camera should be running in this state. */
export function cameraWanted(state: ScannerState): boolean {
  return state.kind === "starting" || state.kind === "scanning";
}
